import { gql } from "lib/apolloClient";
import {
  attachmentFragment,
  staffFragment,
  studentFragment,
  pypElementRatingFragment,
  coachMarkFragment,
  teacherFragments,
  genericFolderFragment,
  attachmentGroupsFragment,
  messageFragment,
  plannerElementNodeFragment,
  plannerElementDepthFragment,
  remarkFragment,
  organizationFragment,
  aiFragment,
} from "./CommonFragments";

export const addApiEventMutation = gql`
  mutation addApiEvent(
    $eventType: API_EVENT_TYPE_ENUM!
    $onlyOnceInADay: Boolean
  ) {
    platform {
      addApiEvent(eventType: $eventType, onlyOnceInADay: $onlyOnceInADay)
    }
  }
`;
export const bulkUploadByFileMutation = gql`
  mutation bulkUploadByFile(
    $url: String!
    $type: BULK_UPLOAD_TYPE_ENUM!
    $organizationId: ID
    $inputs: JSON
  ) {
    platform {
      bulkUploadByFile(
        input: {
          url: $url
          type: $type
          organizationId: $organizationId
          inputs: $inputs
        }
      ) {
        successful
        errors
      }
    }
  }
`;

export const createPlanUpgradeRequestMutation = gql`
  mutation createPlanUpgradeRequest($screen: String) {
    platform {
      createPlanUpgradeRequest(screen: $screen)
    }
  }
`;

export const updateBasicUserDetailsMutation = gql`
  mutation updateBasicUserDetails(
    $firstName: String
    $lastName: String
    $id: ID!
    $email: String
    $profileImage: String
    $showPitchScreen: Boolean
    $firstLoggedInAt: String
    $lastActiveAt: String
    $showClassSelectionScreen: Boolean
    $showBifurcationScreen: Boolean
  ) {
    platform {
      updateUser(
        input: {
          id: $id
          firstName: $firstName
          lastName: $lastName
          profileImage: $profileImage
          email: $email
          firstLoggedInAt: $firstLoggedInAt
          lastActiveAt: $lastActiveAt
          showPitchScreen: $showPitchScreen
          showClassSelectionScreen: $showClassSelectionScreen
          showBifurcationScreen: $showBifurcationScreen
        }
      ) {
        id
        ... on Staff {
          ...staffItem
        }
        ... on Student {
          ...studentItem
        }
      }
    }
  }
  ${studentFragment.basicStudentDetails}
  ${staffFragment.basicStaffDetails}
`;

export const updateAttachmentAndBulkActivateResourcesMutation = gql`
  mutation updateAttachmentAndBulkActivateResources(
    $input: [UpdateAttachmentAndBulkActivateResourcesInput!]
  ) {
    platform {
      updateAttachmentAndBulkActivateResources(input: $input)
    }
  }
`;

export const generateResourceLabelForExistingResourcesMutation = gql`
  mutation generateResourceLabelForExistingResources(
    $input: GenerateResourceLabelForExistingResourcesInput!
  ) {
    platform {
      generateResourceLabelForExistingResources(input: $input) {
        resources {
          doesResourceExist
          requestedLabel
          suggestedLabel
          resourceMapId
        }
        conflicts {
          ids
          totalLabels
          validLabels
          invalidLabels
        }
      }
    }
  }
`;

export const signS3URLMutation = gql`
  mutation signS3URL(
    $fileMimeType: String!
    $fileGroup: String
    $fileExtension: String
    $fileName: String
    $doVirusScanning: Boolean
    $expiry: S3_OBJECT_EXPIRY_ENUM
    $inputFileKey: String
  ) {
    platform {
      signS3URLV2(
        fileMimeType: $fileMimeType
        fileGroup: $fileGroup
        fileExtension: $fileExtension
        fileName: $fileName
        doVirusScanning: $doVirusScanning
        expiry: $expiry
        inputFileKey: $inputFileKey
      ) {
        uploadURL
        displayURL
        signedDisplayURL
        tag
        enforceIfNoneMatch
      }
    }
  }
`;

export const bulkSignS3URLMutation = gql`
  mutation bulkSignS3URL($input: [BulkSignS3URLInput!]) {
    platform {
      bulkSignS3URLV2(input: $input) {
        uploadURL
        displayURL
        tag
        id
        enforceIfNoneMatch
      }
    }
  }
`;

// Mutation to create no-IB subjects in DP
export const createSubjectFromPresetMutation = gql`
  mutation createSubjectFromPreset($input: SubjectVariantsInput!) {
    platform {
      createSubjectFromPreset(input: $input) {
        id
        name
      }
    }
  }
`;

export const markCommentsReadForFieldMutation = gql`
  mutation markCommentsReadForField(
    $parentId: ID!
    $userId: ID!
    $parentType: PARENT_TYPE_ENUM!
  ) {
    platform {
      markCommentsReadForParent(
        input: {
          parents: { id: $parentId, type: $parentType }
          userId: $userId
        }
      )
    }
  }
`;

export const createAttachmentMutation = gql`
  mutation createAttachment(
    $name: String
    $url: String
    $thumbUrl: String
    $mimeType: String
    $type: ATTACHMENT_TYPE_ENUM!
    $parentId: ID
    $createdBy: ID
    $parentType: ATTACHMENT_PARENT_TYPE_ENUM
    $metadata: JSON
    $lockingState: EntityLockingStateInput
    $toddleDoc: ToddleDocInput
  ) {
    platform {
      createAttachment(
        input: {
          url: $url
          mimeType: $mimeType
          type: $type
          thumbUrl: $thumbUrl
          parentId: $parentId
          createdBy: $createdBy
          parentType: $parentType
          name: $name
          metadata: $metadata
          lockingState: $lockingState
          toddleDoc: $toddleDoc
        }
      ) {
        ...attachmentItem
      }
    }
  }
  ${attachmentFragment.attachment}
`;

export const deleteAttachmentMutation = gql`
  mutation deleteAttachments(
    $attachmentIds: [ID]!
    $deleteCondition: DeleteAttachmentConditionInput
    $deleteFromKnowledgeBase: Boolean
  ) {
    platform {
      deleteAttachments(
        attachmentIds: $attachmentIds
        deleteCondition: $deleteCondition
        deleteFromKnowledgeBase: $deleteFromKnowledgeBase
      ) {
        isSuccess
        warning
      }
    }
  }
`;

export const reorderAttachmentGroupMutation = gql`
  mutation reorderAttachmentGroup($input: reorderAttachmentGroupInput!) {
    platform {
      reorderAttachmentGroup(input: $input) {
        response
      }
    }
  }
`;

export const updateNotificationIsNew = gql`
  mutation updateNotificationIsNew($input: NotificationIsNewUpdateInput!) {
    platform {
      updateNotificationIsNew(input: $input)
    }
  }
`;

export const updateNotificationIsRead = gql`
  mutation updateNotificationIsRead($input: NotificationIsReadUpdateInput!) {
    platform {
      updateNotificationIsRead(input: $input)
    }
  }
`;

export const updateNotificationIsReadForUser = gql`
  mutation updateNotificationIsReadForUser(
    $input: NotificationIsReadForUserUpdateInput!
  ) {
    platform {
      updateNotificationIsReadForUser(input: $input)
    }
  }
`;

export const scrapeUrlMutation = gql`
  mutation scrapeUrl($url: String!) {
    platform {
      scrapeUrl(url: $url) {
        description
        image
        title
        url
        date
        publisher
        author
      }
    }
  }
`;

export const createIBPYPElementRatingsMutation = gql`
  mutation createIBPYPElementRatings(
    $elementRatings: [IBPYPElementRatingInput!]
  ) {
    planner {
      createIBPYPElementRatings(input: $elementRatings) {
        ...ratingItem
      }
    }
  }
  ${pypElementRatingFragment.studentElementRating}
`;

export const createIBPYPElementColorsMutation = gql`
  mutation createIBPYPElementColors(
    $input: [createIBPYPElementRatingColorInput!]
  ) {
    planner {
      createIBPYPElementRatingColor(input: $input) {
        id
      }
    }
  }
`;

export const updateStudentAssessmentMarksMutation = gql`
  mutation updateStudentAssessmentMarks(
    $elementRatings: [IBPYPElementRatingInput!]
  ) {
    planner {
      createIBPYPElementRatings(input: $elementRatings) {
        id
      }
    }
  }
`;

export const deleteIBPYPElementRatingsMutation = gql`
  mutation deleteIBPYPElementRatings($input: DeleteIBPYPElementRatingsInput!) {
    planner {
      deleteIBPYPElementRatings(input: $input)
    }
  }
`;

export const deleteMultipleIBPYPElementRatingsMutation = gql`
  mutation deleteMultipleIBPYPElementRatings(
    $input: [DeleteIBPYPElementRatingsInput!]
  ) {
    planner {
      deleteMultipleIBPYPElementRatings(input: $input)
    }
  }
`;

export const updateUserCoachMarkStatusMutation = gql`
  mutation updateUserCoachMarkStatus(
    $userId: ID!
    $coachMark: String!
    $isViewed: Boolean!
    $count: Int
  ) {
    platform {
      updateUserCoachMarkStatus(
        input: {
          userId: $userId
          count: $count
          coachMark: $coachMark
          isViewed: $isViewed
        }
      ) {
        ...coachMarkItem
      }
    }
  }
  ${coachMarkFragment.coachMark}
`;

export const resetPasswordMutation = gql`
  mutation resetPassword(
    $id: ID!
    $password: String!
    $is_password_updated: Boolean
  ) {
    platform {
      updateUserPasswordData(
        input: {
          id: $id
          password: $password
          is_password_updated: $is_password_updated
        }
      ) {
        ...teacherItem
      }
    }
  }
  ${teacherFragments.teacherFeed}
`;

export const upsertSubjectVariantsMutation = gql`
  mutation upsertSubjectVariants($input: [SubjectVariantsInput!]) {
    platform {
      upsertSubjectVariants(input: $input) {
        id
        name
      }
    }
  }
`;

export const generateZoomRefreshTokenMutation = gql`
  mutation generateZoomRefreshToken(
    $accessCode: String!
    $redirectUri: String!
  ) {
    platform {
      generateZoomRefreshToken(
        input: { accessCode: $accessCode, redirectUri: $redirectUri }
      )
    }
  }
`;

export const createGenericFolderMutation = gql`
  mutation createFolder(
    $courseId: ID!
    $name: String!
    $color: String
    $type: FOLDER_TYPE_ENUM!
  ) {
    platform {
      createFolder(
        input: { courseId: $courseId, name: $name, color: $color, type: $type }
      ) {
        warning
        response {
          ...genericFolderItem
        }
      }
    }
  }
  ${genericFolderFragment.genericFolderBasicDetails}
`;

export const updateGenericFolderMutation = gql`
  mutation updateFolder($id: ID!, $name: String, $color: String) {
    platform {
      updateFolder(input: { id: $id, name: $name, color: $color }) {
        warning
        response {
          ...genericFolderItem
        }
      }
    }
  }
  ${genericFolderFragment.genericFolderBasicDetails}
`;

export const deleteGenericFolderMutation = gql`
  mutation deleteFolder($id: ID!) {
    platform {
      deleteFolder(input: { id: $id })
    }
  }
`;

export const createAttachmentGroupsMutation = gql`
  mutation createAttachmentGroups($input: [AttachmentGroupInput!]) {
    platform {
      createAttachmentGroups(input: $input) {
        ...attachmentGroupItem
      }
    }
  }
  ${attachmentGroupsFragment.attachmentGroup}
`;

export const createAttachmentGroupsMutationV2 = gql`
  mutation createAttachmentGroupsV2($input: [AttachmentGroupInput!]) {
    platform {
      createAttachmentGroupsV2(input: $input) {
        response {
          ...attachmentGroupItem
        }
        warning
      }
    }
  }
  ${attachmentGroupsFragment.attachmentGroup}
`;

export const deleteAttachmentGroupsMutation = gql`
  mutation deleteAttachmentGroups($attachmentGroupIds: [ID!]) {
    platform {
      deleteAttachmentGroups(
        input: { attachmentGroupIds: $attachmentGroupIds }
      ) {
        warning
        isSuccess
      }
    }
  }
`;

export const createMessageMutation = gql`
  mutation createMessage(
    $label: String
    $attachments: [AttachmentInput!]
    $parentId: ID!
    $parentType: MESSAGE_PARENT_TYPE_ENUM
    $taggedUsers: [ID!]
    $subEntityType: MESSAGE_SUB_ENTITY_TYPE_ENUM
    $parentMessageId: ID
    $type: String
    $curriculumProgramId: ID
  ) {
    platform {
      createMessage: createMessageV2(
        input: {
          label: $label
          itemType: ATTACHMENTS
          subEntityType: $subEntityType
          attachments: $attachments
          parentId: $parentId
          parentType: $parentType
          taggedUsers: $taggedUsers
          parentMessageId: $parentMessageId
          type: $type
          curriculumProgramId: $curriculumProgramId
        }
      ) {
        ...messageItem
      }
    }
  }
  ${messageFragment.message}
`;

export const updateMessageMutation = gql`
  mutation updateMessage(
    $id: ID!
    $label: String
    $attachments: [AttachmentInput!]
    $taggedUsers: [ID!]
    $type: String
    $isPin: Boolean
  ) {
    platform {
      updateMessage(
        input: {
          id: $id
          label: $label
          itemType: ATTACHMENTS
          attachments: $attachments
          taggedUsers: $taggedUsers
          type: $type
          isPin: $isPin
        }
      ) {
        ...messageItem
      }
    }
  }
  ${messageFragment.message}
`;

export const deleteMessageMutation = gql`
  mutation deleteMessage($id: ID!) {
    platform {
      deleteMessage(input: { id: $id })
    }
  }
`;

export const sendSigninLinkMutation = gql`
  mutation sendSigninLinkMutation($resourceUrl: String, $action: String) {
    platform {
      sendSignInUrlEmail(input: { resourceUrl: $resourceUrl, action: $action })
    }
  }
`;

export const generateThirdPartyRefreshTokenMutation = gql`
  mutation generateThirdPartyRefreshToken(
    $accessCode: String!
    $redirectUri: String!
    $serviceType: THIRD_PARTY_SERVICE_TYPE!
  ) {
    platform {
      generateThirdPartyRefreshToken(
        input: {
          accessCode: $accessCode
          redirectUri: $redirectUri
          serviceType: $serviceType
        }
      ) {
        response {
          id
          serviceType
          accessToken
        }
      }
    }
  }
`;

export const handlePlannerElementNodesMutation = gql`
  mutation handlePlannerElementNodes($input: PlannerElementNodesHandleInput!) {
    planner {
      handlePlannerElementNodes(input: $input)
    }
  }
`;

export const createPlannerElementNodesMutation = gql`
  mutation createPlannerElementNodes($input: PlannerElementNodeCreateInput!) {
    planner {
      createPlannerElementNodes(input: $input) {
        ...plannerElementNodeItem
      }
    }
  }
  ${plannerElementNodeFragment.plannerElementNode}
`;

export const updatePlannerElementNodesMutation = gql`
  mutation updatePlannerElementNodes($input: [PlannerElementNodesEditInput!]) {
    planner {
      updatePlannerElementNodes(input: $input) {
        id
      }
    }
  }
`;

export const deletePlannerElementNodesMutation = gql`
  mutation deletePlannerElementNodes($input: PlannerElementNodesDeleteInput!) {
    planner {
      deletePlannerElementNodes(input: $input)
    }
  }
`;

export const handlePlannerElementSetDepthLabelsMutation = gql`
  mutation handlePlannerElementSetDepthLabels(
    $input: PlannerElementSetDepthLabelsHandleInput!
  ) {
    planner {
      handlePlannerElementSetDepthLabels(input: $input) {
        plannerElementDepthLabels {
          ...plannerElementDepthLabelItem
        }
      }
    }
  }
  ${plannerElementDepthFragment.plannerElementDepthLabels}
`;

export const remarkMutation = gql`
  mutation insertRemarkForProject($input: RemarkInput!) {
    planner {
      upsertRemark(input: $input) {
        ...remarkMessage
      }
    }
  }
  ${remarkFragment.remarkMessageFragment}
`;

export const deleteAcademicHonestyRemarkMutation = gql`
  mutation deleteRemark($id: ID!) {
    planner {
      deleteRemark(input: { id: $id })
    }
  }
`;

export const createOrganizationGlobalConstantsMutation = gql`
  mutation upsertOrganizationGlobalConstant(
    $input: [OrganizationConstantInput!]
  ) {
    platform {
      upsertOrganizationGlobalConstant(input: $input) {
        ...organizationGlobalConstantItem
      }
    }
  }
  ${organizationFragment.organizationGlobalConstants}
`;

export const checkForSimilarityMutation = gql`
  mutation checkForSimilarityMutation($input: GenerateSimilarityReportInput!) {
    integration {
      generateSimilarityReport(input: $input) {
        submissionId
        overallMatchPercentage
        status
        errorCode
      }
    }
  }
`;

export const cancelCheckSimilarityMutation = gql`
  mutation cancelCheckSimilarityMutation($input: CancelSimilarityReportInput!) {
    integration {
      cancelSimilarityReport(input: $input)
    }
  }
`;

export const acceptTurnitinEulaMutation = gql`
  mutation acceptTurnitinEula($input: AcceptTurnitinEulaInput!) {
    integration {
      acceptTurnitinEula(input: $input)
    }
  }
`;

export const deleteIBPYPElementRatingByIdMutation = gql`
  mutation deleteIBPYPElementRatingById($id: ID!) {
    planner {
      deleteIBPYPElementRatingById(id: $id)
    }
  }
`;

export const updateEntityIsReadMutation = gql`
  mutation updateEntityIsRead($id: ID!, $entityType: String!) {
    platform {
      updateEntityIsRead(input: { entityId: $id, entityType: $entityType })
    }
  }
`;

export const updateEntityIsReadInBulkMutation = gql`
  mutation updateEntityIsReadInBulk($input: [UpdateEntityIsReadInput!]) {
    platform {
      updateEntityIsReadInBulk(input: $input) {
        isSuccess
        warning
      }
    }
  }
`;

export const convertPdfToWorkbookMutation = gql`
  mutation convertPdfToWorkbook($url: String!) {
    platform {
      convertPdfToWorkbook(url: $url) {
        isSuccess
        warning
        response {
          url
          numOfPages
          thumbUrl
        }
      }
    }
  }
`;

export const createNestedPlannerFieldsMutation = gql`
  mutation createNestedPlannerFields($input: CreateNestedPlannerFieldsInput!) {
    planner {
      createNestedPlannerFields(input: $input) {
        id
      }
    }
  }
`;

export const deletePlannerFieldMutation = gql`
  mutation deletePlannerField($input: DeletePlannerFieldInput!) {
    planner {
      deletePlannerField(input: $input)
    }
  }
`;

export const deleteStaffStudentRelationMutation = gql`
  mutation deleteRelation($input: StaffStudentRelationInput) {
    platform {
      deleteStaffStudentRelation(input: $input) {
        isSuccess
        warning
      }
    }
  }
`;

export const createAIMessageMutation = gql`
  mutation createAiMessage($input: CreateAiMessageInput!) {
    ai {
      createAiMessage(input: $input) {
        isSuccess
        response {
          ...aiMessageItem
        }
      }
    }
  }
  ${aiFragment.aiMessage}
`;

export const updateAIMessageMutation = gql`
  mutation updateAiMessage($input: UpdateAiMessageInput!) {
    ai {
      updateAiMessage(input: $input) {
        isSuccess
        response {
          ...aiMessageItem
        }
      }
    }
  }
  ${aiFragment.aiMessage}
`;

export const resetAIConversationMutation = gql`
  mutation resetAIConversation($input: ResetAiConversationInput!) {
    ai {
      resetAiConversation(input: $input) {
        isSuccess
        warning
        response {
          id
          entityType
          messages {
            totalCount
            pageInfo {
              hasNextPage
              endCursor
            }
            edges {
              node {
                ...aiMessageItem
              }
            }
          }
        }
      }
    }
  }
  ${aiFragment.aiMessage}
`;

export const createAIConversationMutation = gql`
  mutation createAIConversation($input: CreateAiConversationInput!) {
    ai {
      createAiConversation(input: $input) {
        isSuccess
        response {
          id
          entityType
          metadata
        }
      }
    }
  }
`;

export const updateAIConversationMutation = gql`
  mutation updateAIConversation($input: UpdateAiConversationInput!) {
    ai {
      updateAiConversation(input: $input) {
        isSuccess
        response {
          id
          entityType
          metadata
        }
      }
    }
  }
`;

export const deleteAIConversationMutation = gql`
  mutation deleteAIConversation($input: DeleteAiConversationInput!) {
    ai {
      deleteAiConversation(input: $input) {
        isSuccess
        warning
      }
    }
  }
`;

export const createAttachmentsMutation = gql`
  mutation createAttachments($input: [AttachmentInput!]) {
    platform {
      createAttachment(input: $input) {
        ...attachmentItem
      }
    }
  }
  ${attachmentFragment.attachment}
`;

export const updateAttachmentMutation = gql`
  mutation updateAttachment($input: AttachmentUpdateInput!) {
    platform {
      updateAttachment(input: $input) {
        ...attachmentItem
      }
    }
  }
  ${attachmentFragment.attachment}
`;

export const downloadFamilyInvitesInBulkMutation = gql`
  mutation downloadFamilyInvitesInBulk(
    $input: DownloadFamilyInvitesInBulkInput!
  ) {
    platform {
      downloadFamilyInvitesInBulk(input: $input) {
        isSuccess
        message
        fileUrl
      }
    }
  }
`;

export const generateBulkPdfZipFileMutation = gql`
  mutation generateBulkPdfZipFile($input: GenerateBulkPdfZipFileInput!) {
    platform {
      generateBulkPdfZipFile(input: $input) {
        isSuccess
        message
        fileUrl
      }
    }
  }
`;

export const upsertUserFieldsMutation = gql`
  mutation upsertUserFields($input: [UpsertUserFieldsInput!]!) {
    platform {
      upsertUserFields(input: $input) {
        isSuccess
        message
      }
    }
  }
`;
